/**
 * F38 (Wave 2 Track D) — admin "override venue subscription" modal.
 *
 * Composes Modal + FormField + Select (tier / cadence / status) + Textarea
 * (mandatory reason ≥10) + an audit-logged warning Banner. The override appends
 * a plan_history row + a tier-derived `venue_commission_rates` row server-side.
 * Gated server-side on `venue_subscription:override`; the detail page hides the
 * CTA without it.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, Textarea } from '@bravemove/ui-web';
import {
  SUBSCRIPTION_TIER_CODES,
  VENUE_SUBSCRIPTION_STATUSES,
  BILLING_CADENCES,
  type SubscriptionTierCode,
  type VenueSubscriptionStatus,
  type BillingCadence,
  type AdminOverrideSubscriptionInput,
} from '@bravemove/types';

export interface OverrideSubscriptionDialogProps {
  readonly open: boolean;
  readonly venueLabel: string;
  readonly currentTierCode: SubscriptionTierCode;
  readonly currentCadence: BillingCadence;
  readonly currentStatus: VenueSubscriptionStatus;
  readonly onConfirm: (input: AdminOverrideSubscriptionInput) => Promise<void>;
  readonly onClose: () => void;
}

export function OverrideSubscriptionDialog({
  open,
  venueLabel,
  currentTierCode,
  currentCadence,
  currentStatus,
  onConfirm,
  onClose,
}: OverrideSubscriptionDialogProps) {
  const { t } = useI18n();
  const [tierCode, setTierCode] = useState<SubscriptionTierCode>(currentTierCode);
  const [cadence, setCadence] = useState<BillingCadence>(currentCadence);
  const [status, setStatus] = useState<VenueSubscriptionStatus>(currentStatus);
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setTierCode(currentTierCode);
    setCadence(currentCadence);
    setStatus(currentStatus);
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, currentTierCode, currentCadence, currentStatus]);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const disableSubmit = submitting || !reasonValid;

  const submit = async () => {
    setServerError(null);
    if (!reasonValid) {
      setServerError(
        t('admin:subscription.dialog.reason_required', {
          defaultValue: 'A reason (10–500 characters) is required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ tierCode, cadence, status, reason: reasonTrimmed });
      onClose();
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code?: string }).code
          : null;
      setServerError(
        code === 'SUBSCRIPTION_NOT_FOUND'
          ? t('admin:subscription.dialog.override.error_not_found', {
              defaultValue: 'This venue has no subscription to override.',
            })
          : t('admin:subscription.toast.error_generic', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:subscription.dialog.override.title', {
        defaultValue: 'Override subscription',
      })}
      description={t('admin:subscription.dialog.override.description', {
        defaultValue:
          'Audit-logged. Appends a plan-history entry and a tier-derived commission rate.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="override-subscription-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="override-subscription-confirm"
          >
            {t('admin:subscription.dialog.override.submit', { defaultValue: 'Apply override' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="override-subscription-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-[12px] text-ink-3">
          {t('admin:subscription.detail.venue', { defaultValue: 'Venue' })}:{' '}
          <span className="font-semibold text-ink">{venueLabel}</span>
        </p>

        <div data-testid="override-subscription-warning-banner">
          <Banner
            tone="warning"
            title={t('admin:subscription.dialog.override.banner_title', {
              defaultValue: 'Audit-logged action',
            })}
          >
            {t('admin:subscription.dialog.override.banner_body', {
              defaultValue:
                'Overriding the tier appends a forward-only commission rate. Notifies the venue owner.',
            })}
          </Banner>
        </div>

        <FormField label={t('admin:subscription.detail.tier', { defaultValue: 'Tier' })}>
          <Select
            value={tierCode}
            data-testid="override-subscription-tier"
            aria-label={t('admin:subscription.detail.tier', { defaultValue: 'Tier' })}
            options={SUBSCRIPTION_TIER_CODES.map((c) => ({
              value: c,
              label: t(`admin:subscription.tier.${c}`, { defaultValue: c }),
            }))}
            onChange={(e) => setTierCode(e.target.value as SubscriptionTierCode)}
          />
        </FormField>

        <FormField label={t('admin:subscription.detail.cadence', { defaultValue: 'Cadence' })}>
          <Select
            value={cadence}
            data-testid="override-subscription-cadence"
            aria-label={t('admin:subscription.detail.cadence', { defaultValue: 'Cadence' })}
            options={BILLING_CADENCES.map((c) => ({
              value: c,
              label: t(`admin:subscription.cadence.${c}`, { defaultValue: c }),
            }))}
            onChange={(e) => setCadence(e.target.value as BillingCadence)}
          />
        </FormField>

        <FormField label={t('admin:subscription.detail.status', { defaultValue: 'Status' })}>
          <Select
            value={status}
            data-testid="override-subscription-status"
            aria-label={t('admin:subscription.detail.status', { defaultValue: 'Status' })}
            options={VENUE_SUBSCRIPTION_STATUSES.map((s) => ({
              value: s,
              label: t(`admin:subscription.status.${s}`, { defaultValue: s }),
            }))}
            onChange={(e) => setStatus(e.target.value as VenueSubscriptionStatus)}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="override-subscription-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="override-subscription-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default OverrideSubscriptionDialog;
